import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LineChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line } from 'recharts'
import { formatTime } from '../../../utils/functions'

function getColorMap() {
  return {
    airGapPump: '#40b7ff',
    countRpmSignalsTopArm: '#2f89cc',
    countRpmSignalsBottomArm: '#1e5f95',
    circulationPump: '#8ddfff',
    dryerMotor: '#cc5623',
    fanLcd: '#41c8ce',
    fanPcb: '#38afb5',
  }
}

export default function InputChart(props) {
  const { data, timeFormat } = props
  const { t } = useTranslation()
  const [visibleKeys, setVisibleKeys] = useState([])
  const [selectAll, setSelectAll] = useState(true)
  const [hasUserChangedSelection, setHasUserChangedSelection] = useState(false)
  const colorMap = getColorMap()

  useEffect(() => {
    if (data?.length > 0) {
      const keys = Object.keys(data[0]).filter((key) => key !== 'time')
      setVisibleKeys((prev) => (hasUserChangedSelection ? prev : keys))
    }
  }, [data, hasUserChangedSelection])

  const handleCheckboxChange = (key) => {
    setHasUserChangedSelection(true)
    setVisibleKeys((prev) => {
      if (prev.includes(key)) {
        return prev.length === 1 ? prev : prev.filter((item) => item !== key)
      } else {
        return [...prev, key]
      }
    })
  }

  const handleSelectAllChange = () => {
    setHasUserChangedSelection(true)
    setVisibleKeys(!selectAll ? Object.keys(data[0]).filter((key) => key !== 'time') : [])

    setSelectAll(!selectAll)
  }

  const validData = data?.length > 0

  const renderLegend = ({ onCheckboxChange, onSelectAllChange }) => {
    const legendItems = [
      { key: 'airGapPump', label: t('advance_troubleshooting_context.airGapPump') },
      { key: 'countRpmSignalsTopArm', label: t('advance_troubleshooting_context.countRpmSignalsTopArm') },
      { key: 'countRpmSignalsBottomArm', label: t('advance_troubleshooting_context.countRpmSignalsBottomArm') },
      { key: 'circulationPump', label: t('advance_troubleshooting_context.circulationPump') },
      { key: 'dryerMotor', label: t('advance_troubleshooting_context.dryerMotor') },
      { key: 'fanLcd', label: t('advance_troubleshooting_context.fanLcd') },
      { key: 'fanPcb', label: t('advance_troubleshooting_context.fanPcb') },
    ]

    return (
      <>
        <div className="toggle-container">
          <div className="select-all">
            <input type="checkbox" id="select-all" checked={selectAll} onChange={onSelectAllChange} className="legend-checkbox" />
            <label htmlFor="select-all">{t('select_all')}</label>
          </div>
        </div>
        
        <div className="legend-items">
          {legendItems.map((item) => (
            <div className="legend-item" key={item.key}>
              <input
                type="checkbox"
                id={`checkbox-${item.key}`}
                checked={visibleKeys.includes(item.key)}
                onChange={() => onCheckboxChange(item.key)}
                className="legend-checkbox"
                style={{ backgroundColor: colorMap[item.key] }}
              />
              <label htmlFor={`checkbox-${item.key}`}>{item.label}</label>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <div className="charts-responsive-container">
      <div className="input-chart-wrapper">
        <ResponsiveContainer width="95%" height="90%">
          <LineChart data={data}>
            <CartesianGrid />
            <XAxis tickFormatter={(time) => formatTime(timeFormat, time)} dataKey="time" style={{ fontSize: '0.8em' }} />
            <YAxis yAxisId="left" style={{ fontSize: '0.8em' }} type="number" domain={['auto', 'auto']} />
            {validData &&
              Object.keys(data[0])
                .filter((key) => key !== 'time' && visibleKeys.includes(key))
                .map((key) => (
                  <Line key={key} yAxisId="left" type="monotone" dataKey={key} stroke={colorMap[key]} strokeWidth="2" activeDot={{ r: 8 }} dot={false} />
                ))}
            <Tooltip />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="legend-container">
        {renderLegend({
          onCheckboxChange: handleCheckboxChange,
          onSelectAllChange: handleSelectAllChange,
        })}
      </div>
    </div>
  )
}